import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import founderImg from '../assets/founder.jpeg';

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useInView(ref, threshold = 0.2) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return inView;
}

function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cur = 0;
    const step = target / (duration / 16);
    const t = setInterval(() => {
      cur += step;
      if (cur >= target) { setVal(target); clearInterval(t); }
      else setVal(Math.floor(cur));
    }, 16);
    return () => clearInterval(t);
  }, [active, target, duration]);
  return val;
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const WaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);
const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const FEATURES = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
      </svg>
    ),
    title: 'Concept-First Learning',
    desc: 'Complex concepts distilled into clear, layered modules — no jargon.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    title: 'Indian Market Focus',
    desc: 'Content tailored specifically to NSE, BSE, and Indian regulatory frameworks.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
      </svg>
    ),
    title: 'Trading Psychology',
    desc: 'Discipline over emotions — we teach the mindset before the mechanics.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'No Tips. No Signals.',
    desc: 'Ethical education only. We never sell trading tips or market calls.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
      </svg>
    ),
    title: 'Practical Application',
    desc: 'Real-world scenarios and market analysis embedded into every course.',
  },
];

const OFFERINGS = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: 'Market Insights',
    items: ['Index and sector analysis', 'Clear explanation of market moves'],
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
      </svg>
    ),
    title: 'Courses & Certifications',
    items: ['Beginner to advanced courses', 'College & university training', 'NEP 2020 aligned certifications'],
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
        <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
      </svg>
    ),
    title: 'Skill-Based Learning',
    items: ['Trading psychology', 'Risk management', 'Long-term investing mindset'],
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    title: 'For Students & Colleges',
    items: ['1-day orientation seminars', '30–60 hour certification courses', 'Credit-based NEP 2020 courses'],
  },
];

const Home = () => {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef);
  const learners = useCountUp(500, statsInView);
  const courses = useCountUp(5, statsInView);
  const years = useCountUp(10, statsInView);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="InvFly — Learn the Market. Trade with Logic."
        description="Concept-first financial education built for serious beginners and professionals. No tips, no hype — disciplined, structured learning for Indian markets."
      />

      {/* Hero */}
      <section className="hero">
        <div className="hero-grid"></div>
        <div className="hero-glow"></div>
        <div className="hero-glow2"></div>
        <div className="container hero-inner">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            India's structured market education platform
          </div>
          <h1 className="hero-h1">
            Learn the Market.<br /><span className="ac">Trade with Logic.</span>
          </h1>
          <p className="hero-desc">
            Concept-first financial education built for serious beginners and professionals. No tips, no hype — just disciplined, structured learning for Indian markets.
          </p>
          <div className="hero-cta">
            <Link to="/courses" className="btn-p">Explore Courses <ArrowIcon /></Link>
            <Link to="/signup" className="btn-s">Register Free</Link>
            <a href="https://whatsapp.com/channel/0029VbC9BpM1XqubBPen3T3H" target="_blank" rel="noopener noreferrer" className="btn-g">
              <WaIcon /> Join Community
            </a>
          </div>
          <div className="hero-stats reveal" ref={statsRef}>
            <div className="stat">
              <div className="stat-n">{learners}+</div>
              <div className="stat-l">Learners</div>
            </div>
            <div className="stat">
              <div className="stat-n">{courses}+</div>
              <div className="stat-l">Courses</div>
            </div>
            <div className="stat">
              <div className="stat-n">{years}+</div>
              <div className="stat-l">Yrs Experience</div>
            </div>
            <div className="stat">
              <div className="stat-n" style={{ fontSize: '16px', paddingTop: '4px' }}>Hyderabad</div>
              <div className="stat-l">Based In</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Structured Learning */}
      <section className="sec sec-alt">
        <div className="container">
          <div className="sh reveal">
            <div className="s-tag">Why It Matters</div>
            <div className="s-title">Why <span className="txt-ac">Structured Learning</span> Matters</div>
            <p className="s-desc">Most beginners enter markets through scattered videos and random tips — creating confusion and losses. Structure builds understanding layer by layer.</p>
          </div>
          <div className="feat-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className={`feat-card reveal reveal-d${Math.min(i + 1, 3)}`}>
                <div className="feat-icon">{f.icon}</div>
                <div className="feat-title">{f.title}</div>
                <div className="feat-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="sec sec-alt">
        <div className="container">
          <div className="sh reveal">
            <div className="s-tag">What We Offer</div>
            <div className="s-title">Complete Learning Ecosystem</div>
          </div>
          <div className="feat-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {OFFERINGS.map((o, i) => (
              <div key={i} className={`feat-card reveal reveal-d${Math.min(i + 1, 3)}`}>
                <div className="feat-icon">{o.icon}</div>
                <div className="feat-title">{o.title}</div>
                <ul style={{ listStyle: 'none', marginTop: '10px' }}>
                  {o.items.map((item, j) => (
                    <li key={j} style={{ fontSize: '12.5px', color: 'var(--text-2)', paddingBlock: '4px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ color: 'var(--accent)', fontFamily: 'var(--fm)', fontSize: '11px', marginTop: '2px' }}>→</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="sec">
        <div className="container reveal">
          <div className="sh">
            <div className="s-tag">Leadership</div>
            <div className="s-title">About the Founder</div>
          </div>
          <div className="founder-wrap">
            <div className="founder-img-wrap">
              <img src={founderImg} alt="CA Narasimhaswamy Basavani" className="founder-img" />
            </div>
            <div className="founder-body">
              <div className="founder-badge">Founder & CEO</div>
              <div className="founder-name">CA Narasimhaswamy Basavani</div>
              <div className="founder-role">Chartered Accountant · Financial Markets Trainer · Visiting Faculty</div>
              <p className="founder-bio">With over 10 years of experience in financial markets and academics, Narasimhaswamy built InvFly around a single conviction: real market success comes from understanding — not tips, shortcuts, or hype.</p>
              <p className="founder-bio">He works as a trainer, visiting faculty, and mentor to students and professionals across Hyderabad and partner institutions.</p>
              <div className="founder-acts">
                <a href="https://www.linkedin.com/in/ca-narasimha-swamy-basavani-65443815a/" target="_blank" rel="noopener noreferrer" className="btn-s" style={{ fontSize: '13px', padding: '8px 16px' }}>
                  <LinkedInIcon /> LinkedIn
                </a>
                <Link to="/about" className="btn-s" style={{ fontSize: '13px', padding: '8px 16px' }}>
                  Know More <ArrowIcon />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-sec">
        <div className="container">
          <div className="cta-title">
            Ready to Start Your<br /><span className="txt-ac">Market Learning Journey?</span>
          </div>
          <p className="cta-desc">Join hundreds of learners building disciplined market knowledge with structured education.</p>
          <div className="cta-acts">
            <Link to="/courses" className="btn-p">View Courses <ArrowIcon /></Link>
            <Link to="/signup" className="btn-s">Register Free</Link>
            <Link to="/contact" className="btn-g">Contact Us</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
